import { useTranslation } from "react-i18next";
import { useDocument } from "~/api/v2/useDocument";
import { useDocumentRelease } from "~/api/v2/useDocumentRelease";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { DocumentFieldEditForm } from "./DocumentFieldEditForm";
import { WorkflowScheduleEditForm } from "./WorkflowScheduleEditForm";
import { getEditableFieldLabel } from "./columnRegistry";
import { isArtifactEditableField } from "~/helpers/libraryEditing";
import { dueDateGridDialogClassName } from "~/organisms/DueDateGrid";
import type { DocumentEditableField } from "~/api/v2/requests";
import type { ActiveFieldEdit } from "~/helpers/libraryTypes";

interface FieldEditDialogProps {
  activeEdit: ActiveFieldEdit | null;
  onOpenChange: (isOpen: boolean) => void;
  onSaved: () => Promise<void>;
}

export const FieldEditDialog = ({
  activeEdit,
  onOpenChange,
  onSaved,
}: FieldEditDialogProps) => {
  const { t } = useTranslation();
  const isArtifactField = activeEdit
    ? isArtifactEditableField(activeEdit.field)
    : false;
  const {
    data: document,
    isLoading,
    error,
    mutate: mutateDocument,
  } = useDocument(isArtifactField ? undefined : activeEdit?.documentId);
  const {
    data: release,
    isLoading: isReleaseLoading,
    error: releaseError,
    mutate: mutateRelease,
  } = useDocumentRelease(isArtifactField ? activeEdit?.releaseId : undefined);
  const isDialogLoading = isArtifactField ? isReleaseLoading : isLoading;
  const dialogError = isArtifactField ? releaseError : error;
  const handleSaved = async () => {
    if (isArtifactField) {
      await Promise.all([onSaved(), mutateRelease()]);
      return;
    }

    await Promise.all([onSaved(), mutateDocument()]);
  };

  return (
    <Dialog open={activeEdit !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className={isArtifactField ? dueDateGridDialogClassName : undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {activeEdit &&
              `${t(($) => $.commons.buttons.edit)} ${
                isArtifactField
                  ? t(($) => $.commons.document.reviewDates)
                  : getEditableFieldLabel(activeEdit.field, t)
              }`}
          </DialogTitle>
        </DialogHeader>

        {!activeEdit ? null : isDialogLoading ? (
          <CenterSpinner />
        ) : dialogError ? (
          <NoResult message={t(($) => $.commons.errors.noDocument)} />
        ) : isArtifactField ? (
          release ? (
            <WorkflowScheduleEditForm
              release={release}
              onCancel={() => onOpenChange(false)}
              onSaved={handleSaved}
            />
          ) : (
            <NoResult message={t(($) => $.commons.errors.noDocument)} />
          )
        ) : !document ? (
          <NoResult message={t(($) => $.commons.errors.noDocument)} />
        ) : (
          <DocumentFieldEditForm
            key={activeEdit.field}
            document={document}
            field={activeEdit.field as DocumentEditableField}
            onCancel={() => onOpenChange(false)}
            onSaved={handleSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
